import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import {
  CorpModalScreens,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalAlertProperties,
  ModalButtonType,
  ModalCategoryType,
} from "redmond";
import {
  CloseButton,
  CorporateButton,
  CorporateTypography,
  Icon,
  IconName,
} from "halifax";
import React, { useEffect, useMemo } from "react";
import { WelcomeProps } from "../types";
import capOneLogo from "../../../../../assets/b2b/capone-corporate-logo.svg";
import "./styles.scss";
import { trackEvent } from "../../../../../api/v1/trackEvent";

const welcomePhoto =
  "https://cdn.hoppercot.com/capone-corporate/onboarding/welcome-city-street.jpg";

const DEFAULT_HOTEL_EARN_RATE = 5;

const NON_PRIMARY_VALUE_PROPS = [
  {
    icon: <Icon name={IconName.CorpSavings} />,
    label: (
      <>
        <strong>Save up to 10%</strong> on hotels with our discounted
        corporate rates.
      </>
    ),
  },
  {
    icon: <Icon name={IconName.CorpRewards} />,
    label: (
      <>
        Earn <strong>loyalty points</strong> from your favorite hotels,
        airlines, and rental car brands.
      </>
    ),
  },
  {
    icon: <Icon name={IconName.CorpPlane} />,
    label: (
      <>
        Enjoy exclusive business access to{" "}
        <strong>Southwest airlines.</strong>
      </>
    ),
  },
];

export const Welcome: React.FC<WelcomeProps> = ({
  onClose,
  primaryAction,
  secondaryAction,
  isLoading,
  username,
  isPrimary,
  isSecondaryActionLoading,
  isMobile = false,
  currentAccount,
  primaryVariant = "control",
}) => {
  const isCashCurrency =
    currentAccount?.rewardsBalance?.currency === "Cash";

  const earnRate =
    currentAccount?.earn?.hotelsMultiplier ?? DEFAULT_HOTEL_EARN_RATE;
  const rewardsSymbol = isCashCurrency ? "%" : "X";
  const rewardsCurrency = isCashCurrency ? "cash back" : "miles";

  // Phase 0 revamp experiment variant (Variant B). Only applies to the
  // primary welcome modal; EASE exclusion/flag gating is resolved upstream and
  // surfaced here via `primaryVariant`.
  const isRevampVariant = isPrimary && primaryVariant === "available";

  const valueProps = !isPrimary
    ? NON_PRIMARY_VALUE_PROPS
    : isRevampVariant
    ? [
        {
          icon: <Icon name={IconName.CorpRewards} />,
          label: (
            <>
              <strong>Multiply your {rewardsCurrency}:</strong> Earn an
              unlimited{" "}
              <strong>
                {earnRate}
                {rewardsSymbol} {rewardsCurrency}
              </strong>{" "}
              on hotels and cars booked by any cardholder.
            </>
          ),
        },
        {
          icon: <Icon name={IconName.CorpPercentage} />,
          label: (
            <>
              <strong>Unlock team-wide savings:</strong> Give your whole team
              access to <strong>corporate rates</strong> on flights and hotels.
            </>
          ),
        },
        {
          icon: <Icon name={IconName.CorpPersonSettings} />,
          label: (
            <>
              <strong>Save time managing travel all in one place:</strong>{" "}
              Oversee bookings, set policies, and connect to 24/7 live support.
            </>
          ),
        },
      ]
    : [
        {
          icon: <Icon name={IconName.CorpRewards} />,
          label: (
            <>
              Earn unlimited{" "}
              <strong>
                {earnRate}
                {rewardsSymbol} {rewardsCurrency}
              </strong>{" "}
              on hotels & cars.
            </>
          ),
        },
        {
          icon: <Icon name={IconName.CorpPercentage} />,
          label: (
            <>
              Enjoy business benefits like up to{" "}
              <strong>10% off hotels</strong> with corporate rates and{" "}
              <strong>Southwest booking access.</strong>
            </>
          ),
        },
        {
          icon: <Icon name={IconName.CorpPersonSettings} />,
          label: (
            <>
              Manage travelers, bookings and expenses on{" "}
              <strong>one platform.</strong>
            </>
          ),
        },
      ];

  const isNFU = currentAccount?.customerAccountRole === "Non Financial User";

  const modalTitle = username ? `Welcome, ${username}` : "Welcome";
  const modalSubtitle = isRevampVariant
    ? `Set up your business to earn ${rewardsCurrency} on every trip your team books on Capital One Business Travel.`
    : isNFU || !isPrimary
    ? `Get the most out of your business travel with an easy-to-use interface, 24/7 support and tools you'll actually want to use.`
    : `Get rewarded for growing your business—earn ${rewardsCurrency} on every trip booked with Capital One Business Travel.`;

  // Display node mirrors `modalSubtitle` but bolds the rewards currency in the
  // revamp variant (per Figma). `modalSubtitle` stays a plain string for tracking.
  const modalSubtitleNode: React.ReactNode = isRevampVariant ? (
    <>
      Set up your business to earn{" "}
      <strong style={{ fontWeight: 700 }}>{rewardsCurrency}</strong> on every
      trip your team books on Capital One Business Travel.
    </>
  ) : (
    modalSubtitle
  );

  const trackingType = !isPrimary
    ? "welcome_modal_nonprimary"
    : isRevampVariant
    ? "welcome_modal_primary_available"
    : "welcome_modal_primary";

  const primaryButtonLabel = isRevampVariant ? "Set up my business" : "Book travel";
  const secondaryButtonLabel = isRevampVariant
    ? "Not right now"
    : isPrimary
    ? "Set up my business"
    : "";

  const commonTrackingProperties = useMemo(
    () =>
    ({
      type: trackingType,
      screen: CorpModalScreens.ONBOARDING_WELCOME,
      primary_button: primaryButtonLabel,
      secondary_button: secondaryButtonLabel,
      step: CorpModalScreens.ONBOARDING_WELCOME,
      funnel: "other",
      category: ModalCategoryType.FEATURE,
      modal_subtitle: modalSubtitle,
      modal_title: modalTitle,
      agent_title: modalTitle,
      agent_subtitle: modalSubtitle,
    } satisfies ModalAlertProperties),
    [trackingType, primaryButtonLabel, secondaryButtonLabel, modalSubtitle, modalTitle]
  );

  useEffect(() => {
    trackEvent({
      eventName: MODAL_ALERT,
      properties: commonTrackingProperties,
    });
  }, []);

  // Fires MODAL_ALERT_CHOICE tagging which displayed button (PRIMARY = the
  // emphasized/contained CTA, SECONDARY = the outlined CTA) was clicked, then
  // runs its action. In the control variant PRIMARY = "Book travel"
  // (primaryAction) and SECONDARY = "Set up my business" (secondaryAction); in
  // the revamp variant they are swapped so PRIMARY = "Set up my
  // business" and SECONDARY = "Not right now".
  const onCtaClick = (
    buttonChoice: ModalButtonType,
    action: () => void
  ) => {
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...commonTrackingProperties,
        button_choice: buttonChoice,
      },
    });
    action();
  };

  const primaryCtaAction = isRevampVariant
    ? () => secondaryAction()
    : () => primaryAction();
  const secondaryCtaAction = isRevampVariant
    ? () => primaryAction()
    : () => secondaryAction();
  const isPrimaryCtaLoading = isRevampVariant ? isSecondaryActionLoading : isLoading;
  const isSecondaryCtaLoading = isRevampVariant ? isLoading : isSecondaryActionLoading;

  const primaryCtaButton = (
    <CorporateButton
      size={isMobile ? "large" : "medium"}
      onClick={() => onCtaClick(ModalButtonType.PRIMARY, primaryCtaAction)}
      data-testid="welcome-modal-primary-cta"
      fullWidth={isMobile}
    >
      {isPrimaryCtaLoading ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        primaryButtonLabel
      )}
    </CorporateButton>
  );

  // Secondary (outlined) CTA. Shown for primary users (control: "Set up my
  // business") and in the revamp variant ("Not right now"). Non-primary
  // users have no secondary button.
  const secondaryCtaButton = isPrimary ? (
    <CorporateButton
      size={isMobile ? "large" : "medium"}
      variant="outlined"
      onClick={() => onCtaClick(ModalButtonType.SECONDARY, secondaryCtaAction)}
      fullWidth={isMobile}
    >
      {isSecondaryCtaLoading ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        secondaryButtonLabel
      )}
    </CorporateButton>
  ) : null;

  const mobileWelcomeModal = (
    <Box
      className="welcome-dialog welcome-dialog-mobile"
      style={{
        display: "flex",
        flexDirection: "column",
        margin: "0 auto",
      }}
    >
      <DialogTitle className="dialog-title MuiDialogTitle-root mobile-dialog-title">
        <CloseButton onClick={onClose} />
        <Box className="mobile-image-container">
          <Icon name={IconName.CorpBookingTravelMobile} />
        </Box>
        <Box component="div">
          <CorporateTypography
            variant="h2"
            className="mobile-title"
            component="div"
            id="onboarding-title"
          >
            {modalTitle}
          </CorporateTypography>
          <CorporateTypography
            variant="subtitle2"
            fontWeight="light"
            className="mobile-subtitle"
            component="div"
            id="onboarding-subtitle"
          >
            {modalSubtitleNode}
          </CorporateTypography>
        </Box>
      </DialogTitle>

      <DialogContent style={{ flex: "1 1 auto", overflow: "auto" }}>
        <Box className="mobile-divider" />

        <Box className="mobile-value-props-container">
          {valueProps.map((item, index) => (
            <Box key={index} display="flex" className="mobile-value-prop-item">
              <Box className="mobile-icon-box">{item.icon}</Box>
              <CorporateTypography
                variant="body1"
                fontWeight="light"
                className="mobile-value-prop-text"
              >
                {item.label}
              </CorporateTypography>
            </Box>
          ))}
        </Box>

        <CorporateTypography variant="body2" className="mobile-footer-text">
          {!isNFU &&
            "Any booked trips or traveler profiles are still here—along with powerful new features. "}
          <a
            href="https://businesstravel.capitalone.com/faq/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline", color: "inherit" }}
          >
            Learn more
          </a>{" "}
          about business travel.
        </CorporateTypography>
        <Box className="mobile-divider" />
      </DialogContent>

      <DialogActions className="dialog-actions MuiDialogActions-root mobile-dialog-actions">
        {primaryCtaButton}
        {secondaryCtaButton}
      </DialogActions>
    </Box>
  );
  const desktopWelcomeModal = (
    <Box className="welcome-dialog">
      <CloseButton onClick={onClose} />
      <Box className="welcome-left-panel">
        <DialogTitle className="dialog-title MuiDialogTitle-root">
          <Box className="logo">
            <img
              src={capOneLogo}
              alt="Capital One Business Travel"
              style={{
                height: 52,
                width: "auto",
              }}
            />
          </Box>
          <Box component="div">
            <CorporateTypography
              variant="h1"
              fontWeight="light"
              component="div"
              id="onboarding-title"
            >
              {modalTitle}
            </CorporateTypography>
            <CorporateTypography
              variant="subtitle1"
              fontWeight="light"
              component="div"
              id="onboarding-subtitle"
            >
              {modalSubtitleNode}
            </CorporateTypography>
          </Box>
        </DialogTitle>

        <DialogContent className="dialog-content">
          <Box className="value-props-box">
            {valueProps.map((item, index) => (
              <Box key={index} display="flex" className="value-prop-item">
                <Box className="icon-box">{item.icon}</Box>
                <CorporateTypography
                  variant="subtitle2"
                  fontWeight="light"
                  className="value-prop-item-text"
                >
                  {item.label}
                </CorporateTypography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions className="dialog-actions MuiDialogActions-root">
          {secondaryCtaButton}
          {primaryCtaButton}
        </DialogActions>
      </Box>
      <Box className="welcome-photo-panel">
        <img src={welcomePhoto} alt="" decoding="async" />
      </Box>
    </Box>
  );

  return isMobile ? mobileWelcomeModal : desktopWelcomeModal;
};

export default Welcome;
